import axios from "axios";
import type { CurrentUser } from "@workspace/types";
import type { DomainWorkspaceMatch, WorkspaceData } from "@workspace/types";

const API_URL = "https://api.dev.yolk.coach/_internal/app";

function makeServerClient(accessToken: string) {
  return axios.create({
    baseURL: process.env.NEXT_PUBLIC_YOLK_API_URL || API_URL,
    // The layouts await these calls before returning any HTML, so a hung
    // backend must not hang the whole page — bail out and let the client
    // fetch path take over.
    timeout: 3000,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
}

export async function serverGetCurrentUser(
  accessToken: string,
): Promise<CurrentUser> {
  const client = makeServerClient(accessToken);
  return (await client.get<CurrentUser>("/users/me")).data;
}

export async function serverGetWorkspaces(
  accessToken: string,
): Promise<WorkspaceData[]> {
  const client = makeServerClient(accessToken);
  return (await client.get<{ items: WorkspaceData[] }>("/workspaces")).data
    .items;
}

export async function serverLookupWorkspaceByDomain(
  accessToken: string,
): Promise<DomainWorkspaceMatch | null> {
  const client = makeServerClient(accessToken);
  try {
    const response = await client.get<{
      match: DomainWorkspaceMatch | null;
    }>("/workspaces/domain-match");
    return response.data.match ?? null;
  } catch (error) {
    // No match is a 200 with a null match. A 403 means the backend won't vouch
    // for an unverified address, and a 404 that the endpoint isn't deployed —
    // neither is a match, so both fall through to the role question.
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    if (status === 403 || status === 404) {
      return null;
    }
    throw error;
  }
}
